/**
 * Webhooks RTK Query slice. Hits two microservices:
 *   - notification: webhook CRUD + the per-webhook notification audit logs
 *   - transformation: the Customize/Monaco snippet flow
 * Action-history list + "View Log" reuse `historyApi` + `auditApi.parseAuditUrl`.
 *
 * Wire shapes (locked with `toEqual` in `webhooks.test.ts`; webhook writes
 * mutate shared dev data so they are NEVER fired live).
 *
 *   GET    /notification/v1/search?&subscriberIds={id}                         → { subscriptions }
 *   GET    /notification/v1/{webhookId}                                        → Webhook
 *   GET    /notification/v1/{webhookId}/auditLogs/notifications?resource=FULL&includeResource=true
 *   GET    {notification}/{parsed auditUrl path}                               → { events }
 *   POST   /notification/v1                          body Webhook (no id)      (subscribe)
 *   PUT    /notification/v1                          body Webhook              (update/toggle/test/attach)
 *   DELETE /notification/v1/{webhookId}
 *   GET    /transformation/v1/transformations/{transformationId}              → { snippets }
 *   POST   /transformation/v1/transformation         body CustomizationInput   → { transformationId, snippetId }
 *   PATCH  /transformation/v1/transformation/{id}/createSnippet  body Customization → { transformationId, snippetId }
 *   PATCH  /transformation/v1/transformation/{id}/{snippetId}/promote
 *   POST   /transformation/v1/transformation/test     body { payload, snippetCode } → { transformationResult }
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { portalBaseQuery } from '@lib/baseQuery';
import { dispatchCacheAction } from '@lib/rtkQueryDispatch';
import { services } from '@config/services';
import { parseAuditUrl, type AuditResponse } from '@features/audit/api';
import type {
  Webhook,
  WebhookInput,
  WebhooksSearchResponse,
  NotificationAuditResponse,
  TransformationDetails,
  SnippetMutationResponse,
  CustomizationInput,
  TransformationTestInput,
  TransformationTestResult,
  TransformationTestResponse,
} from './types';

/**
 * Pure request builders — exported so the wire-shape lock test asserts the
 * produced `{ url, method, body }` with `toEqual`.
 */
export const webhooksRequestBuilders = {
  /** List — note the leading `&` before `subscriberIds` (matched LIVE). */
  searchWebhooks: ({ subscriberId }: { subscriberId: string }) => ({
    url: `${services.notification}/v1/search?&subscriberIds=${encodeURIComponent(subscriberId)}`,
    method: 'GET' as const,
  }),

  /** Fetch a subscriber's webhooks via the simple subscriber filter endpoint.
   * Distinct from `searchWebhooks` — uses `/v1?subscriberId=`,
   * not the search index `/v1/search?&subscriberIds=`.
   * Used by the StoreFront uninstall path to find webhooks to delete. */
  getWebhooksBySubscriber: ({ subscriberId }: { subscriberId: string }) => ({
    url: `${services.notification}/v1?subscriberId=${encodeURIComponent(subscriberId)}`,
    method: 'GET' as const,
  }),

  getWebhookDetails: ({ webhookId }: { webhookId: string }) => ({
    url: `${services.notification}/v1/${encodeURIComponent(webhookId)}`,
    method: 'GET' as const,
  }),

  getNotificationAuditLogs: ({ webhookId }: { webhookId: string }) => ({
    url: `${services.notification}/v1/${encodeURIComponent(webhookId)}/auditLogs/notifications?resource=FULL&includeResource=true`,
    method: 'GET' as const,
  }),

  /** "View Log" audit fetch — DIFF→FULL + includeResource=true so the response carries a payload. */
  getWebhookAuditInfo: (auditUrl: string) => ({
    url: `${services.notification}/${parseAuditUrl(auditUrl).path.replaceAll('DIFF', 'FULL')}&includeResource=true`,
    method: 'GET' as const,
  }),

  /** Create (subscribe). NOTE rank-3 — never fired live. */
  subscribeToWebhook: (body: WebhookInput) => ({
    url: `${services.notification}/v1`,
    method: 'POST' as const,
    body,
  }),

  /** Update (also backs toggle / test / customize-attach). NOTE rank-3 — never fired live. */
  updateWebhook: (body: WebhookInput) => ({
    url: `${services.notification}/v1`,
    method: 'PUT' as const,
    body,
  }),

  deleteWebhookSubscription: ({ webhookId }: { webhookId: string }) => ({
    url: `${services.notification}/v1/${encodeURIComponent(webhookId)}`,
    method: 'DELETE' as const,
  }),

  getTransformationDetails: ({ transformationId }: { transformationId: string }) => ({
    url: `${services.transformation}/v1/transformations/${encodeURIComponent(transformationId)}`,
    method: 'GET' as const,
  }),

  createTransformation: (body: CustomizationInput) => ({
    url: `${services.transformation}/v1/transformation`,
    method: 'POST' as const,
    body,
  }),

  createSnippet: ({ transformationId, body }: { transformationId: string; body: CustomizationInput }) => ({
    url: `${services.transformation}/v1/transformation/${encodeURIComponent(transformationId)}/createSnippet`,
    method: 'PATCH' as const,
    body,
  }),

  promoteTransformation: ({ transformationId, snippetId }: { transformationId: string; snippetId: string }) => ({
    url: `${services.transformation}/v1/transformation/${encodeURIComponent(transformationId)}/${encodeURIComponent(snippetId)}/promote`,
    method: 'PATCH' as const,
  }),

  /** Run a snippet against a sample payload. Non-mutating, so safe to fire live. */
  testTransformation: (body: TransformationTestInput) => ({
    url: `${services.transformation}/v1/transformation/test`,
    method: 'POST' as const,
    body,
  }),
};

/** Unwrap the list response (`{ subscriptions }`; tolerate a bare array). */
export function unwrapWebhooks(response: WebhooksSearchResponse | Webhook[]): WebhooksSearchResponse {
  if (Array.isArray(response)) return { subscriptions: response };
  return { subscriptions: response.subscriptions ?? [] };
}

export const webhooksApi = createApi({
  reducerPath: 'webhooksApi',
  baseQuery: portalBaseQuery,
  tagTypes: ['Webhook', 'WebhookList', 'WebhookAudit'],
  endpoints: (builder) => ({
    /** GET webhooks by subscriber via the simple filter endpoint (`/v1?subscriberId=`).
     * Used by the StoreFront uninstall orchestration to find and delete
     * per-transformation subscriptions. Not the same as `searchWebhooks`
     * (`/v1/search?&subscriberIds=`) — different backend path, different
     * indexing semantics. Not cached aggressively (no providesTags) because it
     * is only needed transiently during the uninstall flow. */
    getWebhooksBySubscriber: builder.query<WebhooksSearchResponse, { subscriberId: string }>({
      query: (args) => webhooksRequestBuilders.getWebhooksBySubscriber(args),
      transformResponse: unwrapWebhooks,
    }),

    /** GET the subscriber's webhook list; unwraps `{ subscriptions }`. */
    searchWebhooks: builder.query<WebhooksSearchResponse, { subscriberId: string }>({
      query: (args) => webhooksRequestBuilders.searchWebhooks(args),
      transformResponse: unwrapWebhooks,
      providesTags: (result) =>
        result
          ? [
              ...result.subscriptions.map((w) => ({ type: 'Webhook' as const, id: w.id })),
              { type: 'WebhookList' as const, id: 'LIST' },
            ]
          : [{ type: 'WebhookList' as const, id: 'LIST' }],
    }),

    /** GET a single webhook; identity transform (explicit per the unwrap rule). */
    getWebhookDetails: builder.query<Webhook, { webhookId: string }>({
      query: (args) => webhooksRequestBuilders.getWebhookDetails(args),
      transformResponse: (response: Webhook) => response,
      providesTags: (_r, _e, { webhookId }) => [{ type: 'Webhook', id: webhookId }],
    }),

    /** GET the per-webhook notification audit logs (the Logs card). */
    getNotificationAuditLogs: builder.query<NotificationAuditResponse, { webhookId: string }>({
      query: (args) => webhooksRequestBuilders.getNotificationAuditLogs(args),
      transformResponse: (response: NotificationAuditResponse | { events?: NotificationAuditResponse['events'] }) => ({
        events: response.events ?? [],
        total: (response as NotificationAuditResponse).total,
      }),
      providesTags: (_r, _e, { webhookId }) => [{ type: 'WebhookAudit', id: webhookId }],
    }),

    /** Lazy "View Log" fetch for an Action-History row's auditUrl. */
    getWebhookAuditInfo: builder.query<AuditResponse, string>({
      query: (auditUrl) => webhooksRequestBuilders.getWebhookAuditInfo(auditUrl),
    }),

    /** GET a transformation's snippets (Customize edit/view). */
    getTransformationDetails: builder.query<TransformationDetails, { transformationId: string }>({
      query: (args) => webhooksRequestBuilders.getTransformationDetails(args),
      transformResponse: (response: TransformationDetails) => response,
    }),

    // ── Mutations (never fired live; wire-locked) ──
    subscribeToWebhook: builder.mutation<Webhook, WebhookInput>({
      query: (body) => webhooksRequestBuilders.subscribeToWebhook(body),
      invalidatesTags: [{ type: 'WebhookList', id: 'LIST' }],
    }),

    updateWebhook: builder.mutation<Webhook, WebhookInput>({
      query: (body) => webhooksRequestBuilders.updateWebhook(body),
      // List refresh only. The detail is patched from the body (onQueryStarted)
      // instead of refetching the eventually-consistent detail, which could
      // clobber the write with a stale snapshot.
      invalidatesTags: [{ type: 'WebhookList', id: 'LIST' }],
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return; // PUT failed — leave the cache untouched; callers toast.
        }
        if (!body.id) return;
        dispatchCacheAction(
          dispatch,
          webhooksApi.util.updateQueryData('getWebhookDetails', { webhookId: body.id }, (draft) => {
            Object.assign(draft, body);
          }),
        );
      },
    }),

    /** Active toggle. No invalidation/refetch — the search index is eventually-consistent; the list holds an optimistic local copy. */
    setWebhookEnabled: builder.mutation<Webhook, WebhookInput>({
      query: (body) => webhooksRequestBuilders.updateWebhook(body),
    }),

    deleteWebhookSubscription: builder.mutation<void, { webhookId: string }>({
      query: (args) => webhooksRequestBuilders.deleteWebhookSubscription(args),
      invalidatesTags: [{ type: 'WebhookList', id: 'LIST' }],
    }),

    createTransformation: builder.mutation<SnippetMutationResponse, CustomizationInput>({
      query: (body) => webhooksRequestBuilders.createTransformation(body),
    }),

    createSnippet: builder.mutation<SnippetMutationResponse, { transformationId: string; body: CustomizationInput }>({
      query: (args) => webhooksRequestBuilders.createSnippet(args),
    }),

    promoteTransformation: builder.mutation<void, { transformationId: string; snippetId: string }>({
      query: (args) => webhooksRequestBuilders.promoteTransformation(args),
    }),

    /** Run a snippet against a sample payload; unwraps the `transformationResult` envelope. */
    testTransformation: builder.mutation<TransformationTestResult, TransformationTestInput>({
      query: (body) => webhooksRequestBuilders.testTransformation(body),
      transformResponse: (response: TransformationTestResponse) => response.transformationResult,
    }),
  }),
});

export const {
  useGetWebhooksBySubscriberQuery,
  useLazyGetWebhooksBySubscriberQuery,
  useSearchWebhooksQuery,
  useGetWebhookDetailsQuery,
  useLazyGetWebhookDetailsQuery,
  useGetNotificationAuditLogsQuery,
  useLazyGetWebhookAuditInfoQuery,
  useGetTransformationDetailsQuery,
  useSubscribeToWebhookMutation,
  useUpdateWebhookMutation,
  useSetWebhookEnabledMutation,
  useDeleteWebhookSubscriptionMutation,
  useCreateTransformationMutation,
  useCreateSnippetMutation,
  usePromoteTransformationMutation,
  useTestTransformationMutation,
} = webhooksApi;
